import { createContext, useContext, type ReactNode } from 'react';

import { isApiError } from '@platform/api-client';

import { apiClient } from './client';
import { createIntegrationsClient, type IntegrationsClient } from './integrations';
import { createMockIntegrationsClient } from './integrations.mock';

/**
 * Which `IntegrationsClient` the integrations screens talk to (MASTER_PLAN M3.9).
 *
 * Unlike the other seams in this app, the default here is the **real** client: M3 shipped
 * the endpoints, so `/integrations` is the first screen in `apps/admin` that talks to the
 * API. Three modes, decided once at module load:
 *
 * | Build | `VITE_USE_MOCKS` | Client |
 * |---|---|---|
 * | any | `true` | the mock, and only the mock |
 * | `vite dev` | unset | the real client, falling back to the mock the first time a call cannot reach the API |
 * | `vite build` | unset | the real client, and only the real client |
 *
 * ## Why the fallback is development-only
 *
 * A production bundle that quietly answered with fixtures when the API was unreachable
 * would show a clinic a healthy Tebra that does not exist. That is a worse failure than
 * an error state, so the fallback is compiled out of a production build; what is left is
 * the real client and the screen's own error handling (rules §17.4).
 *
 * ## The CRM routes ride this seam too (M3.12)
 *
 * `GET|PUT .../highlevel/mapping`, `POST .../highlevel/sync-now` and the per-patient
 * `GET|POST /admin/patients/:id/crm*` are all on `IntegrationsClient` rather than on a
 * `CrmClient` of their own. They are settings and actions on one integration install, they
 * share the one fallback latch — a page whose mapping came from the API and whose patient
 * panel came from fixtures would be incoherent — and `CrmPatientStatus` can therefore be
 * mounted on the patient record with no extra provider, because `useIntegrationsClient()`
 * falls back to this module's own client when there is no context above it.
 *
 * The fallback latches on `NETWORK_ERROR` alone — the code `@platform/api-client` invents
 * when `fetch` itself rejected. A `401`, a `403` or a `500` came *from* the API and is the
 * screen's to show; swapping in fixtures on a permission error would hide exactly the
 * thing the operator needs to see.
 */

const useMocks = import.meta.env.VITE_USE_MOCKS === 'true';

function defaultClient(): IntegrationsClient {
  if (useMocks) return createMockIntegrationsClient();
  const real = createIntegrationsClient(apiClient);
  if (!import.meta.env.DEV) return real;
  return withMockFallback(real, createMockIntegrationsClient());
}

const IntegrationsClientContext = createContext<IntegrationsClient | null>(null);

export function IntegrationsClientProvider({
  client,
  children,
}: {
  client: IntegrationsClient;
  children: ReactNode;
}) {
  return (
    <IntegrationsClientContext.Provider value={client}>
      {children}
    </IntegrationsClientContext.Provider>
  );
}

/** The app's client, created once. Tests and the provider override it. */
export const integrationsClient: IntegrationsClient = defaultClient();

export function useIntegrationsClient(): IntegrationsClient {
  return useContext(IntegrationsClientContext) ?? integrationsClient;
}

/**
 * Wraps every method so that the first unreachable-API call switches this session to the
 * mock and re-runs the call. One latch, not one per method: a page whose list came from
 * the mock and whose detail came from the API would be incoherent.
 */
function withMockFallback(real: IntegrationsClient, mock: IntegrationsClient): IntegrationsClient {
  type AnyMethod = (...args: never[]) => Promise<unknown>;
  let active: IntegrationsClient = real;

  function proxy<Key extends keyof IntegrationsClient>(method: Key): IntegrationsClient[Key] {
    return (async (...args: never[]): Promise<unknown> => {
      if (active !== real) return (mock[method] as AnyMethod)(...args);
      try {
        return await (real[method] as AnyMethod)(...args);
      } catch (error) {
        if (!isApiError(error) || error.code !== 'NETWORK_ERROR') throw error;
        console.warn(
          `The API did not answer ${String(method)}; the integrations screens are using fixtures for this session.`,
        );
        active = mock;
        return (mock[method] as AnyMethod)(...args);
      }
    }) as IntegrationsClient[Key];
  }

  return {
    list: proxy('list'),
    get: proxy('get'),
    putCredentials: proxy('putCredentials'),
    deleteCredentials: proxy('deleteCredentials'),
    patchSettings: proxy('patchSettings'),
    test: proxy('test'),
    enable: proxy('enable'),
    disable: proxy('disable'),
    listHealth: proxy('listHealth'),
    listCalls: proxy('listCalls'),
    getCrmMapping: proxy('getCrmMapping'),
    putCrmMapping: proxy('putCrmMapping'),
    crmSyncNow: proxy('crmSyncNow'),
    getPatientCrm: proxy('getPatientCrm'),
    resyncPatientCrm: proxy('resyncPatientCrm'),
    verifyTotp: proxy('verifyTotp'),
  };
}
